import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { productsAPI, toxicologyAPI } from '@/utils/api';
import { Upload, Download, FileText, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import DipBreadcrumb from '@/components/DipBreadcrumb';
import DipStepFooter from '@/components/DipStepFooter';

const Toxicology = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [toxicology, setToxicology] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadForm, setUploadForm] = useState({
    report_date: new Date().toISOString().split('T')[0],
    toxicologist_name: '',
    file: null,
  });

  useEffect(() => {
    loadData();
  }, [productId]);

  const loadData = async () => {
    try {
      const prodRes = await productsAPI.getOne(productId);
      setProduct(prodRes.data);

      try {
        const toxRes = await toxicologyAPI.getByProduct(productId);
        setToxicology(toxRes.data);
      } catch (error) {
        console.log('No toxicology report');
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadForm.file) {
      toast.error('Sélectionnez un fichier');
      return;
    }

    const loadingToast = toast.loading('Upload du rapport en cours...');

    try {
      const formData = new FormData();
      formData.append('product_id', productId);
      formData.append('report_date', uploadForm.report_date);
      formData.append('toxicologist_name', uploadForm.toxicologist_name);
      formData.append('file', uploadForm.file);

      console.log('Uploading toxicology:', {
        productId,
        date: uploadForm.report_date,
        filename: uploadForm.file.name,
        size: uploadForm.file.size
      });

      const response = await toxicologyAPI.upload(formData);
      console.log('Upload response:', response.data);
      
      toast.dismiss(loadingToast);
      toast.success('Rapport toxicologique ajouté avec succès');
      
      setUploadForm({
        report_date: new Date().toISOString().split('T')[0],
        toxicologist_name: '',
        file: null,
      });
      document.getElementById('tox-file-input').value = '';
      
      await loadData();
    } catch (error) {
      toast.dismiss(loadingToast);
      console.error('Error uploading toxicology:', error);
      console.error('Error details:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload');
    }
  };

  const handleDownload = async () => {
    try {
      const res = await toxicologyAPI.download(toxicology.id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', toxicology.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Téléchargement lancé');
    } catch (error) {
      console.error('Error downloading:', error);
      toast.error('Erreur lors du téléchargement');
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="skeleton-shimmer h-64 rounded-lg"></div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <DipBreadcrumb productId={productId} productName={product?.name} currentStep="toxicology" />

      <div className="mb-8">
        <h1 className="text-4xl font-semibold text-foreground mb-2 tracking-tight">
          Évaluation toxicologique
        </h1>
        <p className="text-base text-muted-foreground leading-relaxed">
          {product?.name} - Rapport du toxicologue
        </p>
      </div>

      {toxicology ? (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle2 className="text-green-600" size={24} />
              Rapport toxicologique présent
            </CardTitle>
            <CardDescription>Document archivé et versionné</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-start justify-between p-4 border border-border rounded-md">
              <div className="flex items-center gap-3">
                <FileText className="text-primary" size={32} strokeWidth={1.5} />
                <div>
                  <p className="font-medium text-foreground">{toxicology.filename}</p>
                  <p className="text-sm text-muted-foreground">Date du rapport: {toxicology.report_date}</p>
                  {toxicology.toxicologist_name && (
                    <p className="text-sm text-muted-foreground">Toxicologue: {toxicology.toxicologist_name}</p>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    Uploadé le: {new Date(toxicology.uploaded_at).toLocaleDateString('fr-FR')}
                  </p>
                </div>
              </div>
              <Button
                data-testid="btn-download-toxicology"
                variant="outline"
                onClick={handleDownload}
                className="gap-2"
              >
                <Download size={18} />
                Télécharger
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Uploader le rapport toxicologique</CardTitle>
            <CardDescription>
              Rapport d'évaluation de la sécurité du produit par un toxicologue qualifié
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleUpload} className="space-y-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">
                  Date du rapport
                </label>
                <Input
                  data-testid="input-report-date"
                  type="date"
                  value={uploadForm.report_date}
                  onChange={(e) => setUploadForm({ ...uploadForm, report_date: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">
                  Nom du toxicologue (optionnel)
                </label>
                <Input
                  data-testid="input-toxicologist-name"
                  placeholder="Dr. Marie Dupont"
                  value={uploadForm.toxicologist_name}
                  onChange={(e) => setUploadForm({ ...uploadForm, toxicologist_name: e.target.value })}
                />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">
                  Fichier PDF
                </label>
                <Input
                  id="tox-file-input"
                  data-testid="input-toxicology-file"
                  type="file"
                  accept=".pdf"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      console.log('File selected:', file.name, file.size, 'bytes');
                      setUploadForm({ ...uploadForm, file });
                    }
                  }}
                  required
                />
                {uploadForm.file && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Fichier sélectionné : {uploadForm.file.name} ({(uploadForm.file.size / 1024).toFixed(1)} KB)
                  </p>
                )}
              </div>
              <Button data-testid="btn-upload-toxicology" type="submit" className="w-full gap-2">
                <Upload size={18} />
                Uploader le rapport
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="p-6 bg-muted rounded-lg">
        <h3 className="font-semibold text-foreground mb-2">Information importante</h3>
        <p className="text-sm text-muted-foreground leading-relaxed">
          Le rapport toxicologique est obligatoire pour la conformité réglementaire (CE 1223/2009). Il doit être réalisé
          par un toxicologue qualifié et contenir l'évaluation de la sécurité du produit fini.
        </p>
      </div>

      <DipStepFooter productId={productId} currentStep="toxicology" />
    </div>
  );
};

export default Toxicology;
